import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import LexiconModal from './LexiconModal';
import GrammarModal from './GrammarModal';

// Book name to abbreviation mapping for Hebrew (OT)
const HEBREW_BOOK_ABBREVS = {
  'genesis': 'Gen', 'exodus': 'Exo', 'leviticus': 'Lev', 'numbers': 'Num',
  'deuteronomy': 'Deu', 'joshua': 'Jos', 'judges': 'Jdg', 'ruth': 'Rut',
  '1 samuel': '1Sa', '2 samuel': '2Sa', '1 kings': '1Ki', '2 kings': '2Ki',
  '1 chronicles': '1Ch', '2 chronicles': '2Ch', 'ezra': 'Ezr', 'nehemiah': 'Neh',
  'esther': 'Est', 'job': 'Job', 'psalms': 'Psa', 'psalm': 'Psa',
  'proverbs': 'Pro', 'ecclesiastes': 'Ecc', 'song of solomon': 'Sng',
  'isaiah': 'Isa', 'jeremiah': 'Jer', 'lamentations': 'Lam', 'ezekiel': 'Ezk',
  'daniel': 'Dan', 'hosea': 'Hos', 'joel': 'Joe', 'amos': 'Amo',
  'obadiah': 'Oba', 'jonah': 'Jon', 'micah': 'Mic', 'nahum': 'Nah',
  'habakkuk': 'Hab', 'zephaniah': 'Zep', 'haggai': 'Hag', 'zechariah': 'Zec',
  'malachi': 'Mal'
};

// Book name to abbreviation mapping for Greek (NT)
const GREEK_BOOK_ABBREVS = {
  'matthew': 'Mat', 'mark': 'Mrk', 'luke': 'Luk', 'john': 'Jhn',
  'acts': 'Act', 'romans': 'Rom', '1 corinthians': '1Co', '2 corinthians': '2Co',
  'galatians': 'Gal', 'ephesians': 'Eph', 'philippians': 'Php', 'colossians': 'Col',
  '1 thessalonians': '1Th', '2 thessalonians': '2Th', '1 timothy': '1Ti',
  '2 timothy': '2Ti', 'titus': 'Tit', 'philemon': 'Phm', 'hebrews': 'Heb',
  'james': 'Jas', '1 peter': '1Pe', '2 peter': '2Pe', '1 john': '1Jn',
  '2 john': '2Jn', '3 john': '3Jn', 'jude': 'Jud', 'revelation': 'Rev'
};

const isHebrewBook = (bookName) => {
  const lower = bookName?.toLowerCase();
  return !!HEBREW_BOOK_ABBREVS?.[lower];
};

const getBookAbbrev = (bookName, isHebrew) => {
  const lower = bookName?.toLowerCase();
  if (isHebrew) return HEBREW_BOOK_ABBREVS?.[lower] || null;
  return GREEK_BOOK_ABBREVS?.[lower] || null;
};

const HEBREW_ROWS = [
  { id: 'hebrew_word', label: 'Hebrew', alwaysVisible: true, isRTL: true },
  { id: 'transliteration', label: 'Translit.', alwaysVisible: false },
  { id: 'translation', label: 'Translation', alwaysVisible: false },
  { id: 'd_strongs', label: "Strong\'s", alwaysVisible: false },
  { id: 'grammar', label: 'Grammar', alwaysVisible: false },
  { id: 'meaning_variant', label: 'Variant', alwaysVisible: false },
];

const GREEK_ROWS = [
  { id: 'greek_word', label: 'Greek', alwaysVisible: true, isRTL: false },
  { id: 'transliteration', label: 'Translit.', alwaysVisible: false },
  { id: 'translation', label: 'Translation', alwaysVisible: false },
  { id: 'd_strongs', label: "Strong\'s", alwaysVisible: false },
  { id: 'grammar', label: 'Grammar', alwaysVisible: false },
  { id: 'gloss', label: 'Gloss', alwaysVisible: false },
  { id: 'spanish', label: 'Spanish', alwaysVisible: false },
];

const InterlinearDisplay = ({ book, chapter, verse, onClose }) => {
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isHebrew, setIsHebrew] = useState(false);
  const [visibleRows, setVisibleRows] = useState(new Set(['hebrew_word', 'greek_word']));
  const containerRef = React.useRef(null);
  const [lexiconStrongs, setLexiconStrongs] = useState(null);
  const [grammarCode, setGrammarCode] = useState(null);

  useEffect(() => {
    const fetchInterlinear = async () => {
      if (!book || !chapter || !verse) return;

      setLoading(true);
      setError(null);

      const hebrew = isHebrewBook(book);
      setIsHebrew(hebrew);

      const abbrev = getBookAbbrev(book, hebrew);
      if (!abbrev) {
        setError(`No interlinear data available for ${book}.`);
        setLoading(false);
        return;
      }

      try {
        if (hebrew) {
          const { data, error: dbError } = await supabase?.from('interlinear_hebrew')?.select('id, word_position, heb_ref, text_type, hebrew_word, transliteration, translation, d_strongs, grammar, meaning_variant, s_strong, alt_strongs')?.eq('book_abbrev', abbrev)?.eq('chapter', chapter)?.eq('verse', verse)?.order('word_position', { ascending: true });

          if (dbError) throw dbError;
          setWords(data || []);
          setVisibleRows(new Set(['hebrew_word']));
        } else {
          const { data, error: dbError } = await supabase?.from('interlinear_greek')?.select('id, word_position, word_type, greek_word, transliteration, translation, d_strongs, grammar, dictionary_form, gloss, editions, meaning_variant, spanish, sub_meaning, s_strong, alt_strongs')?.eq('book_abbrev', abbrev)?.eq('chapter', chapter)?.eq('verse', verse)?.order('word_position', { ascending: true });

          if (dbError) throw dbError;
          setWords(data || []);
          setVisibleRows(new Set(['greek_word']));
        }
      } catch (err) {
        console.error('Error fetching interlinear data:', err);
        setError('Failed to load interlinear data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchInterlinear();
  }, [book, chapter, verse]);

  const toggleRow = (rowId) => {
    setVisibleRows(prev => {
      const next = new Set(prev);
      if (next?.has(rowId)) {
        next?.delete(rowId);
      } else {
        next?.add(rowId);
      }
      return next;
    });
  };

  const rowDefs = isHebrew ? HEBREW_ROWS : GREEK_ROWS;
  const primaryRowId = isHebrew ? 'hebrew_word' : 'greek_word';

  // Active rows = always-visible + toggled-on rows
  const activeRows = rowDefs?.filter(r => r?.alwaysVisible || visibleRows?.has(r?.id));

  // For Hebrew, words are read right-to-left
  const displayWords = isHebrew ? [...words]?.reverse() : words;

  /**
   * Parse a d_strongs field value into individual Strong's tokens.
   */
  const parseStrongsTokens = (raw) => {
    if (!raw) return [];
    const cleaned = String(raw)?.replace(/[{}]/g, '');
    const parts = cleaned?.split(/[/\\\s]+/)?.filter(Boolean);
    const tokens = [];
    for (const part of parts) {
      const subTokens = part?.match(/[HG]\d+[A-Za-z]*/g) || [];
      if (subTokens?.length > 0) {
        tokens?.push(...subTokens);
      } else if (/^[HG]\d+[A-Za-z]*$/?.test(part)) {
        tokens?.push(part);
      }
    }
    return tokens;
  };

  const isGrammaticalParticle = (token) => {
    const m = token?.match(/^H(9\d{3})$/);
    if (!m) return false;
    const num = parseInt(m?.[1], 10);
    return num >= 9001 && num <= 9028;
  };

  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center gap-2 text-muted-foreground">
        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-sm">Loading interlinear data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-sm text-muted-foreground flex items-center gap-2">
        <Icon name="Info" size={14} />
        <span>{error}</span>
      </div>
    );
  }

  if (!words?.length) {
    return (
      <div className="p-4 text-sm text-muted-foreground flex items-center gap-2">
        <Icon name="Info" size={14} />
        <span>No interlinear data found for this verse.</span>
      </div>
    );
  }

  return (
    <div className="p-3" ref={containerRef}>
      {/* Header with row toggles */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-1.5">
          <Icon name={isHebrew ? 'BookOpen' : 'Languages'} size={14} className="text-primary" />
          <span className="text-sm font-semibold text-foreground">
            {isHebrew ? 'Hebrew Interlinear' : 'Greek Interlinear'}
          </span>
        </div>

        {/* Row visibility toggles */}
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-xs text-muted-foreground mr-1">Show:</span>
          {rowDefs?.filter(r => !r?.alwaysVisible)?.map(row => {
            const isOn = visibleRows?.has(row?.id);
            return (
              <button
                key={row?.id}
                onClick={() => toggleRow(row?.id)}
                className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border transition-all duration-150 ${
                  isOn
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card text-primary border-border hover:bg-muted'
                }`}
              >
                <Icon name={isOn ? 'Minus' : 'Plus'} size={10} />
                {row?.label}
              </button>
            );
          })}
        </div>
      </div>
      {/* Word cards — flex-wrap layout, each word is a vertical card */}
      <div
        className={`flex flex-wrap gap-2 ${isHebrew ? 'flex-row-reverse justify-end' : 'flex-row'}`}
        dir={isHebrew ? 'rtl' : 'ltr'}
      >
        {displayWords?.map((word, idx) => {
          const wordKey = word?.id || idx;

          return (
            <div
              key={wordKey}
              className="flex flex-col bg-card border border-border rounded-lg"
              style={{ minWidth: '90px', maxWidth: '130px', flex: '0 0 auto' }}
              dir="ltr"
            >
              {activeRows?.map((row, rowIdx) => {
                const isPrimary = row?.id === primaryRowId;
                const value = word?.[row?.id];
                const isEmpty = !value || String(value)?.trim() === '';

                // Strong's row
                if (row?.id === 'd_strongs') {
                  const tokens = parseStrongsTokens(value);
                  return (
                    <div
                      key={row?.id}
                      className={`px-2 py-1 ${rowIdx < activeRows?.length - 1 ? 'border-b border-border' : ''}`}
                    >
                      {tokens?.length > 0 ? (
                        <div className="flex flex-wrap gap-0.5">
                          {tokens?.map((token, tIdx) => {
                            const isParticle = isGrammaticalParticle(token);
                            return isParticle ? (
                              <span
                                key={tIdx}
                                className="text-xs text-muted-foreground italic"
                                title={`${token} — grammatical particle`}
                              >
                                {token}
                              </span>
                            ) : (
                              <button
                                key={tIdx}
                                onClick={() => setLexiconStrongs(token)}
                                className="text-xs font-semibold text-primary hover:underline text-left cursor-pointer"
                                title={`Open lexicon for ${token}`}
                              >
                                {token}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">—</span>
                      )}
                    </div>
                  );
                }

                // Grammar row
                if (row?.id === 'grammar') {
                  return (
                    <div
                      key={row?.id}
                      className={`px-2 py-1 ${rowIdx < activeRows?.length - 1 ? 'border-b border-border' : ''}`}
                    >
                      {!isEmpty ? (
                        <button
                          onClick={() => setGrammarCode(String(value))}
                          className="text-xs font-semibold text-primary hover:underline text-left cursor-pointer w-full truncate"
                          title={`Explain grammar code: ${value}`}
                        >
                          {String(value)}
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">—</span>
                      )}
                    </div>
                  );
                }

                // Primary word row (Hebrew / Greek)
                if (isPrimary) {
                  return (
                    <div
                      key={row?.id}
                      className={`px-2 py-2 bg-primary/10 ${rowIdx < activeRows?.length - 1 ? 'border-b border-border' : ''}`}
                      dir={row?.isRTL ? 'rtl' : 'ltr'}
                    >
                      <span
                        className={`block text-center font-bold text-foreground ${isHebrew ? 'text-2xl' : 'text-xl'} leading-tight`}
                        style={row?.isRTL ? { fontFamily: 'serif', letterSpacing: '0.02em' } : {}}
                      >
                        {isEmpty ? '—' : String(value)}
                      </span>
                    </div>
                  );
                }

                // All other rows (transliteration, translation, gloss, etc.)
                return (
                  <div
                    key={row?.id}
                    className={`px-2 py-1 ${rowIdx < activeRows?.length - 1 ? 'border-b border-border' : ''}`}
                  >
                    <span
                      className={`block text-center text-sm leading-snug ${
                        isEmpty ? 'text-muted-foreground italic' : 'text-foreground'
                      }`}
                      title={isEmpty ? '' : String(value)}
                    >
                      {isEmpty ? '—' : String(value)}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      {/* Footer note */}
      <div className="mt-3 text-[10px] text-muted-foreground">
        Data: STEPBible.org / Tyndale House Cambridge (CC BY 4.0)
      </div>
      {/* Lexicon Modal — portaled to body to escape overflow-hidden/animation stacking context */}
      {lexiconStrongs && createPortal(
        <LexiconModal
          strongsNumber={lexiconStrongs}
          onClose={() => setLexiconStrongs(null)}
        />,
        document.body
      )}
      {/* Grammar Modal — portaled to body */}
      {grammarCode && createPortal(
        <GrammarModal
          grammarCode={grammarCode}
          onClose={() => setGrammarCode(null)}
        />,
        document.body
      )}
    </div>
  );
};

export default InterlinearDisplay;
